/** Settings masters under HR Master Data — one URL per former tab/section. */
export type HrMasterDataSettingsKind = 'master-tab' | 'holiday-tab' | 'devices-page';

export type HrMasterDataSettingsEntry = {
  key: string;
  label: string;
  description: string;
  kind: HrMasterDataSettingsKind;
  /** Tab id passed to HrMasterDataPage or HrHolidaysPage. */
  tab?: string;
  legacyRoutes: string[];
};

export const HR_MASTER_DATA_SETTINGS_ENTRIES: HrMasterDataSettingsEntry[] = [
  {
    key: 'leave-types',
    label: 'Leave types',
    description: 'Define leave categories, calendar colors, and accrual policies per company.',
    kind: 'master-tab',
    tab: 'leave',
    legacyRoutes: ['/hr/master-data?tab=leave'],
  },
  {
    key: 'employment-types',
    label: 'Employment types',
    description: 'Contract types used when hiring and changing employment.',
    kind: 'master-tab',
    tab: 'employment',
    legacyRoutes: ['/hr/master-data?tab=employment'],
  },
  {
    key: 'document-types',
    label: 'Document types',
    description: 'Categories for employee document uploads and expiry tracking.',
    kind: 'master-tab',
    tab: 'documents',
    legacyRoutes: ['/hr/master-data?tab=documents'],
  },
  {
    key: 'business-units',
    label: 'Business units',
    description: 'Top-level divisions used to group departments and reporting.',
    kind: 'master-tab',
    tab: 'businessUnits',
    legacyRoutes: ['/hr/master-data?tab=businessUnits'],
  },
  {
    key: 'departments',
    label: 'Departments',
    description: 'Functional teams employees belong to — optional parent and business unit.',
    kind: 'master-tab',
    tab: 'departments',
    legacyRoutes: ['/hr/master-data?tab=departments'],
  },
  {
    key: 'grades',
    label: 'Grades',
    description: 'Job levels for compensation bands and designation mapping.',
    kind: 'master-tab',
    tab: 'grades',
    legacyRoutes: ['/hr/master-data?tab=grades'],
  },
  {
    key: 'designations',
    label: 'Designations',
    description: 'Job titles linked to a department and grade.',
    kind: 'master-tab',
    tab: 'designations',
    legacyRoutes: ['/hr/master-data?tab=designations'],
  },
  {
    key: 'overtime-types',
    label: 'Overtime types',
    description: 'Classify overtime by calendar context — each company defines its own.',
    kind: 'master-tab',
    tab: 'overtime',
    legacyRoutes: ['/hr/master-data?tab=overtime'],
  },
  {
    key: 'shift-types',
    label: 'Shift types',
    description: 'Categories for shift definitions (fixed, flexible, rotational, etc.).',
    kind: 'master-tab',
    tab: 'shiftTypes',
    legacyRoutes: ['/hr/master-data?tab=shiftTypes'],
  },
  {
    key: 'shift-definitions',
    label: 'Shift definitions',
    description: 'Named shifts with hours and calendar colors for employee assignment.',
    kind: 'master-tab',
    tab: 'shifts',
    legacyRoutes: ['/hr/master-data?tab=shifts'],
  },
  {
    key: 'expense-types',
    label: 'Expense types',
    description: 'Categories for employee expense claims and reimbursements.',
    kind: 'master-tab',
    tab: 'expenseTypes',
    legacyRoutes: ['/hr/master-data?tab=expenseTypes'],
  },
  {
    key: 'asset-types',
    label: 'Asset types',
    description: 'Equipment categories employees can request — laptop, phone, monitor, etc.',
    kind: 'master-tab',
    tab: 'assetTypes',
    legacyRoutes: ['/hr/master-data?tab=assetTypes'],
  },
  {
    key: 'asset-requests',
    label: 'Asset requests',
    description: 'Submit and track employee equipment requests with approval workflow.',
    kind: 'master-tab',
    tab: 'assetRequests',
    legacyRoutes: ['/hr/master-data?tab=assetRequests', '/hr/asset-requests'],
  },
  {
    key: 'pipeline-stages',
    label: 'Pipeline stages',
    description: 'Recruitment kanban columns — name, order, and color per company.',
    kind: 'master-tab',
    tab: 'pipelineStages',
    legacyRoutes: ['/hr/master-data?tab=pipelineStages'],
  },
  {
    key: 'holiday-calendar',
    label: 'Holiday calendar',
    description: 'Company public holidays, premium rates, and timesheet treatment.',
    kind: 'holiday-tab',
    tab: 'calendar',
    legacyRoutes: ['/hr/holidays', '/hr/holidays?tab=calendar'],
  },
  {
    key: 'holiday-types',
    label: 'Holiday types',
    description: 'Holiday categories your company uses on the work calendar.',
    kind: 'holiday-tab',
    tab: 'types',
    legacyRoutes: ['/hr/holidays?tab=types'],
  },
  {
    key: 'biometric-devices',
    label: 'Biometric devices',
    description: 'Biometric, RFID, QR, and face recognition attendance connectors.',
    kind: 'devices-page',
    legacyRoutes: ['/hr/devices', '/settings/hr/devices'],
  },
];

const LEGACY_PATH_LOOKUP = new Map<string, string>();

for (const entry of HR_MASTER_DATA_SETTINGS_ENTRIES) {
  const route = `/settings/hr-master-data/${entry.key}`;
  for (const legacy of entry.legacyRoutes) {
    LEGACY_PATH_LOOKUP.set(legacy.split('?')[0].replace(/\/+$/, '') || '/', route);
    LEGACY_PATH_LOOKUP.set(legacy.replace(/\/+$/, '') || '/', route);
  }
}

/** Map legacy HR master/holiday/shift URLs (with optional ?tab=) to settings routes. */
export function resolveHrMasterDataLegacySettingsPath(pathname: string, search = ''): string | undefined {
  const path = pathname.replace(/\/+$/, '') || '/';
  const full = `${path}${search}`;
  const exact = LEGACY_PATH_LOOKUP.get(full) ?? LEGACY_PATH_LOOKUP.get(path);
  if (exact) return exact;

  if (path === '/hr/master-data') {
    const tab = new URLSearchParams(search).get('tab');
    if (tab) {
      const match = HR_MASTER_DATA_SETTINGS_ENTRIES.find((e) => e.kind === 'master-tab' && e.tab === tab);
      if (match) return `/settings/hr-master-data/${match.key}`;
    }
    return '/settings#hr-master-data';
  }

  if (path === '/hr/holidays') {
    const tab = new URLSearchParams(search).get('tab') ?? 'calendar';
    const match = HR_MASTER_DATA_SETTINGS_ENTRIES.find((e) => e.kind === 'holiday-tab' && e.tab === tab);
    if (match) return `/settings/hr-master-data/${match.key}`;
    return '/settings#hr-master-data';
  }

  return undefined;
}
